import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Loader2, UserPlus, Lock, Unlock, UserX, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorWorkers = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [togglingAvailability, setTogglingAvailability] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    phone: "",
    personal_number: "",
    address: "",
    user_type: "worker",
    is_manpower_worker: true
  });

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setWorkers(response.data);
    } catch (error) {
      toast.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handleToggleAvailability = async (workerId, currentStatus) => {
    setTogglingAvailability(workerId);
    try {
      const response = await axios.put(`${API}/users/${workerId}/toggle-availability`);
      setWorkers(workers.map(w => 
        w.id === workerId 
          ? { ...w, is_available: response.data.is_available } 
          : w
      ));
      toast.success(response.data.is_available 
        ? "Worker is now available for assignments" 
        : "Worker is now hidden from Admin selection"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update availability");
    } finally {
      setTogglingAvailability(null);
    }
  };

  const handleDeactivateWorker = async (workerId, workerName) => {
    if (!window.confirm(`Remove access for ${workerName}? They will no longer be able to log in, but their timesheet history will be preserved.`)) return;
    try {
      await axios.put(`${API}/users/${workerId}/deactivate`);
      toast.success(`${workerName} access has been removed`);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove access");
    }
  };

  const handleDeleteWorker = async (workerId, workerName) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE "${workerName}"?\n\n` +
      `This will permanently delete this team member and ALL their associated data (timesheets, assignments, etc.).\n\n` +
      `This action CANNOT be undone.\n\n` +
      `If you just want to revoke their access while keeping their history, use "Remove Access" instead.`
    );
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      `Are you absolutely sure?\n\n` +
      `You are about to permanently delete ${workerName} and all their data.`
    );
    if (!doubleConfirm) return;
    
    try {
      await axios.delete(`${API}/users/${workerId}?confirm_delete_timesheets=true`);
      toast.success(`${workerName} permanently deleted`);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team member");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users/create-direct`, formData);
      toast.success("Team member created! They will receive a notification email.");
      setDialogOpen(false);
      setFormData({
        email: "",
        name: "",
        password: "",
        phone: "",
        personal_number: "",
        address: "",
        user_type: "worker",
        is_manpower_worker: true
      });
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team member");
    }
  };

  const filteredWorkers = workers.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="contractor-workers">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">MANPOWER TEAM MEMBERS</h1>
          <p className="text-[#52525B]">Team members assigned to Gavs Bemanning AB</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => setDialogOpen(true)}
          data-testid="add-team-member-button"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
        <Input
          placeholder="Search team members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-manpower-workers"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : (
            <Table className="table-aggreko">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Personal Number</TableHead>
                  <TableHead>Availability</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.map((worker) => (
                  <TableRow key={worker.id} className={worker.is_available === false ? "bg-gray-50 opacity-60" : ""}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {worker.name}
                        <Badge className="badge-manpower text-xs">Manpower</Badge>
                        {worker.is_available === false && (
                          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">Hidden</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{worker.email}</TableCell>
                    <TableCell>{worker.phone || "-"}</TableCell>
                    <TableCell>{worker.personal_number || "-"}</TableCell>
                    <TableCell>
                      <Badge className={worker.is_available !== false ? "bg-green-500 text-white" : "bg-gray-400 text-white"}>
                        {worker.is_available !== false ? "Available" : "Not Available"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleAvailability(worker.id, worker.is_available)}
                          disabled={togglingAvailability === worker.id}
                          data-testid={`toggle-availability-${worker.id}`}
                          className={worker.is_available !== false 
                            ? "border-red-200 text-red-600 hover:bg-red-50" 
                            : "border-green-200 text-green-600 hover:bg-green-50"
                          }
                        >
                          {togglingAvailability === worker.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : worker.is_available !== false ? (
                            <><Lock className="w-4 h-4 mr-1" />Hide</>
                          ) : (
                            <><Unlock className="w-4 h-4 mr-1" />Show</>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-gray-700 text-xs"
                          onClick={() => handleDeactivateWorker(worker.id, worker.name)}
                          title="Remove Access (keeps history)"
                          data-testid={`deactivate-worker-${worker.id}`}
                        >
                          <UserX className="w-3 h-3 mr-1" />
                          Remove Access
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 text-xs"
                          onClick={() => handleDeleteWorker(worker.id, worker.name)}
                          title="Delete Permanently"
                          data-testid={`delete-worker-${worker.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredWorkers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[#A1A1AA]">
                      No manpower team members found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Team Member Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="input-label">Full Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Erik Svensson"
                  data-testid="worker-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="input-label">Email Address *</Label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="erik@example.se"
                  data-testid="worker-email-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="input-label">Initial Password *</Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  data-testid="worker-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">Share this with the worker. They will be prompted to change it on first login.</p>
              </div>
              <div>
                <Label className="input-label">Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+46 70 123 4567"
                  data-testid="worker-phone-input"
                />
              </div>
              <div>
                <Label className="input-label">Personal Number</Label>
                <Input
                  value={formData.personal_number}
                  onChange={(e) => setFormData({ ...formData, personal_number: e.target.value })}
                  placeholder="YYYYMMDD-XXXX"
                  data-testid="worker-personal-number-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="input-label">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street, City"
                  data-testid="worker-address-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-worker-button">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Team Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorWorkers;
